import React from 'react';
import { motion } from 'framer-motion';

const WhyChooseUs = () => {
    return (
        <section className="bg-blue-50 border-t-[5px] border-white py-10 md:py-16 relative overflow-hidden text-slate-900 font-sans">

            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">

                {/* LEFT COLUMN - Text Content */}
                <div className="space-y-12">

                    {/* Main Header */}
                    <div>
                        <h2 className="text-4xl lg:text-6xl font-bold uppercase tracking-wider mb-6 text-slate-900" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
                            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Us?</span>
                        </h2>
                        <p className="text-2xl lg:text-3xl leading-tight font-medium text-slate-700" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
                            Our team aims to deliver engaging brand messages. And craft outstanding work in every pixel.
                        </p>
                    </div>

                    {/* Our Process */}
                    <div>
                        <h3 className="text-xl font-bold uppercase tracking-widest text-blue-600 mb-4" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
                            Our Process
                        </h3>
                        <p className="text-slate-600 leading-relaxed mb-6 font-light" style={{ fontFamily: '"Outfit", sans-serif' }}>
                            We believe building the best digital brand awareness and consideration begins with a website design experience that is ownable, durable and unique. Brands today need to exceed both industry and societal expectations in order to WOW audiences and be seen as a force in their market.
                        </p>
                        <div className="pl-4 border-l-2 border-blue-500">
                            <p className="text-slate-800 font-medium mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>Our agency methodology is built on 3 cores:</p>
                            <ul className="text-slate-600 space-y-1 text-sm list-disc pl-5" style={{ fontFamily: '"Outfit", sans-serif' }}>
                                <li>Unlocking brand value</li>
                                <li>Humanizing the data</li>
                                <li>Designing to WOW</li>
                            </ul>
                        </div>
                        <p className="text-slate-500 mt-4 italic text-sm" style={{ fontFamily: '"Outfit", sans-serif' }}>We call this Data Meets Design.</p>
                    </div>

                    {/* Technology */}
                    <div>
                        <h3 className="text-xl font-bold uppercase tracking-widest text-purple-600 mb-4" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
                            Technology
                        </h3>
                        <p className="text-slate-600 leading-relaxed font-light" style={{ fontFamily: '"Outfit", sans-serif' }}>
                            Fast-moving trends require rock-solid technical skills. To stay ahead of the curve, we keep learning and experimenting constantly on the latest front-end and back-end development frameworks or stacks. From custom CMS solutions to modern frameworks offering flexible database access layers, we build systems that make complex websites simpler and easier to manage. We build fast and grow solid.
                        </p>
                    </div>

                </div>

                {/* RIGHT COLUMN - Visuals & Collaboration */}
                <div className="space-y-12">

                    {/* Collaboration Text */}
                    <div>
                        <h3 className="text-xl font-bold uppercase tracking-widest text-slate-900 mb-4" style={{ fontFamily: '"Rajdhani", sans-serif' }}>
                            Collaboration
                        </h3>
                        <p className="text-slate-600 leading-relaxed font-light" style={{ fontFamily: '"Outfit", sans-serif' }}>
                            We’re committed to partnering with our clients. We understand and respect that they will always know more about their business and markets than we are able to know. We invest the time to co-create an approach to projects with our client stakeholders and form the right collection of our talented people to mobilize the project. We won't be starting from scratch, rather we will leverage what we have learned together as partners.
                        </p>
                    </div>

                    {/* Image Collage Grid */}
                    <div className="grid grid-cols-2 gap-4 relative">
                        {/* Decorative background blob */}
                        <div className="absolute inset-0 bg-blue-100 blur-[80px] rounded-full pointer-events-none opacity-60"></div>

                        {/* Image 1: Team Meeting */}
                        <motion.div
                            whileHover={{ y: -5 }}
                            className="bg-white p-2 rounded-2xl shadow-xl border border-slate-100"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                alt="Collaboration"
                                className="w-full h-48 object-cover rounded-xl"
                            />
                        </motion.div>

                        {/* Image 2: Tech/Cyberpunk */}
                        <motion.div
                            whileHover={{ y: -5 }}
                            className="bg-white p-2 rounded-2xl shadow-xl mt-8 border border-slate-100"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                alt="Tech"
                                className="w-full h-40 object-cover rounded-xl"
                            />
                            <div className="p-3">
                                <div className="h-1.5 w-full bg-slate-200 rounded-full mb-2 overflow-hidden">
                                    <div className="h-full bg-blue-600 w-[85%]"></div>
                                </div>
                                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Project Velocity</p>
                            </div>
                        </motion.div>

                        {/* Image 3: Analytics/Data */}
                        <motion.div
                            whileHover={{ y: -5 }}
                            className="bg-white p-2 rounded-2xl shadow-xl border border-slate-100"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                alt="Analytics"
                                className="w-full h-32 object-cover rounded-xl"
                            />
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider px-2 pt-2 pb-1 font-bold">Data Driven Results</p>
                        </motion.div>

                        {/* Image 4: Creative Office */}
                        <motion.div
                            whileHover={{ y: -5 }}
                            className="bg-white p-2 rounded-2xl shadow-xl -mt-8 border border-slate-100"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                                alt="Team"
                                className="w-full h-56 object-cover rounded-xl"
                            />
                        </motion.div>

                    </div>
                </div>

            </div>
        </section>
    );
};

export default WhyChooseUs;
